import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, FieldError, Input, TextField } from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccountPicker } from '../accountPicker';
import { amountErrorText } from '../../journalEntryMeta';
import type { JournalEntryFormModel } from '../../useJournalEntryForm';

type JournalLineRowProps = {
    form: JournalEntryFormModel;
    index: number;
};

/**
 * One editable journal line: account picker + debit + credit + memo + remove.
 * A line is one-sided — typing on one amount side disables (and the model
 * clears) the other. Enter on any amount/memo in the last row adds a new line
 * so ten lines can be entered without the mouse.
 */
export const JournalLineRow: React.FC<JournalLineRowProps> = ({
    form,
    index,
}) => {
    const { t } = useTranslation('pages');
    const line = form.lines[index];
    const parse = form.lineParses[index];

    // Row hooks must run unconditionally; bail to null only after them.
    const onAmountKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                if (line) {
                    form.onRowEnter(line.key);
                }
            }
        },
        [form, line],
    );

    if (!line || !parse) {
        return null;
    }

    const debitActive = line.debit.trim() !== '';
    const creditActive = line.credit.trim() !== '';
    const disabled = form.isEditingPosted;
    const rowNumber = index + 1;

    const debitError = amountErrorText(parse.debitError, t);
    const creditError = amountErrorText(parse.creditError, t);

    return (
        <div
            className="grid grid-cols-1 gap-2 rounded-lg bg-surface-secondary p-3 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_minmax(0,10rem)_auto] sm:items-start sm:gap-3 sm:bg-transparent sm:p-0"
            {...getNodeTestIdProp(`journal-line-row-${line.key}`)}
        >
            <AccountPicker
                ariaLabel={t('journalEntry.lines.accountAria', {
                    line: rowNumber,
                })}
                isDisabled={disabled}
                onChange={(accountId) =>
                    form.onLineAccountChange(line.key, accountId)
                }
                options={form.options}
                placeholder={t('journalEntry.lines.accountPlaceholder')}
                slot={line.key}
                value={line.accountId}
            />

            <TextField
                aria-label={t('journalEntry.lines.debitAria', {
                    line: rowNumber,
                })}
                className="flex flex-col gap-1"
                isDisabled={disabled || creditActive}
                isInvalid={parse.debitError !== null}
                onChange={(value) => form.onLineDebitChange(line.key, value)}
                value={line.debit}
                {...getNodeTestIdProp(`journal-line-debit-${line.key}`)}
            >
                <Input
                    className="text-right tabular-nums"
                    inputMode="decimal"
                    onKeyDown={onAmountKeyDown}
                    placeholder="0.00"
                />
                {/* Wired to the input via aria-describedby by React-Aria. */}
                <FieldError className="text-xs text-danger">
                    {debitError}
                </FieldError>
            </TextField>

            <TextField
                aria-label={t('journalEntry.lines.creditAria', {
                    line: rowNumber,
                })}
                className="flex flex-col gap-1"
                isDisabled={disabled || debitActive}
                isInvalid={parse.creditError !== null}
                onChange={(value) => form.onLineCreditChange(line.key, value)}
                value={line.credit}
                {...getNodeTestIdProp(`journal-line-credit-${line.key}`)}
            >
                <Input
                    className="text-right tabular-nums"
                    inputMode="decimal"
                    onKeyDown={onAmountKeyDown}
                    placeholder="0.00"
                />
                <FieldError className="text-xs text-danger">
                    {creditError}
                </FieldError>
            </TextField>

            <TextField
                aria-label={t('journalEntry.lines.memoAria', {
                    line: rowNumber,
                })}
                isDisabled={disabled}
                onChange={(value) => form.onLineMemoChange(line.key, value)}
                value={line.memo}
                {...getNodeTestIdProp(`journal-line-memo-${line.key}`)}
            >
                <Input
                    onKeyDown={onAmountKeyDown}
                    placeholder={t('journalEntry.lines.memoPlaceholder')}
                />
            </TextField>

            <Button
                aria-label={t('journalEntry.lines.removeAria', {
                    line: rowNumber,
                })}
                className={cn(form.lines.length <= 1 && 'invisible')}
                isDisabled={disabled || form.lines.length <= 1}
                isIconOnly
                onPress={() => form.onRemoveLine(line.key)}
                variant="ghost"
                {...getNodeTestIdProp(`journal-line-remove-${line.key}`)}
            >
                <Icon aria-hidden icon="gravity-ui:trash-bin" />
            </Button>
        </div>
    );
};
